<template>
  <div class="craps-scene">
    <div ref="surface" class="craps-scene__surface" role="img" :aria-label="description"></div>
    <div v-if="$slots.top" class="craps-scene__top"><slot name="top"></slot></div>
    <div v-if="$slots.bottom" class="craps-scene__bottom"><slot name="bottom"></slot></div>
    <div v-if="shoutAnnouncement" :key="shoutAnnouncement.sequence" class="craps-scene__announcement craps-scene__shout" :class="{ 'is-fading': shoutFading }" role="status" aria-live="assertive" aria-atomic="true">
      <span v-if="shoutAnnouncement.text" class="craps-scene__shooter">{{ shoutAnnouncement.shooter }} rolls!</span>
      <strong>{{ shoutAnnouncement.text ? '“' + shoutAnnouncement.text + '”' : shoutAnnouncement.shooter + ' rolls!' }}</strong>
    </div>
    <div v-else-if="table && table.phase === 'LOTTERY' && presentingSequence === null" class="craps-scene__announcement craps-scene__lottery" role="status" aria-live="polite" aria-atomic="true">
      <span class="craps-scene__shooter">Shooter lottery open!</span>
      <strong>Type !ROLL now!</strong>
      <span class="craps-scene__lottery-detail">{{ table.deadlineAt ? 'Enter before the countdown ends. You could be the next shooter!' : 'Be the next shooter. The first entry starts the countdown.' }}</span>
    </div>
    <div v-if="graphics !== 'ready'" class="craps-scene__notice" role="status">
      {{ graphics === 'failed' ? '3D graphics unavailable. The live table and controls remain below.' : 'Preparing the 3D table…' }}
      <button v-if="graphics === 'failed'" class="craps-button" @click="mountScene">Retry graphics</button>
    </div>
    <span v-if="backend" class="craps-scene__backend">{{ backend === 'webgpu' ? 'WebGPU' : 'WebGL2' }}</span>
    <section v-if="cameraPanel && cameraSettings" class="craps-scene__camera" aria-label="Camera tuning">
      <div class="craps-scene__camera-heading"><strong>Camera tuning</strong><button class="craps-button" aria-label="Close camera tuning" @click="cameraPanel = false">×</button></div>
      <label class="craps-label">Camera tilt · {{ cameraTilt }}°
        <input type="range" :min="cameraSettings.min" :max="cameraSettings.max" step="1" :value="cameraTilt" :aria-valuetext="cameraTilt + ' degrees above the table'" @input="setCameraTilt(Number($event.target.value))">
      </label>
      <div class="craps-scene__camera-scale"><span>Low angle</span><span>Top down</span></div>
      <div class="craps-scene__camera-footer"><span>Only this view · ~ to hide</span><button class="craps-button" @click="setCameraTilt(cameraSettings.default)">Reset</button></div>
    </section>
  </div>
</template>
<script>
import { loadSceneModule, compareDecimal } from '../../lib/craps-client'
import { SHOUT_HOLD_MS, SHOUT_FADE_MS, SHOUT_ROLL_DELAY_MS } from '../../../../shared/lib/craps/presentation-timing'
const MAX_QUEUED_ROLLS = 2
export default {
  props: { table: Object, snap: Number, connection: String, interactive: Boolean, insets: Object },
  data () { return { graphics: 'loading', backend: '', cameraPanel: false, cameraSettings: null, cameraTilt: null, shoutAnnouncement: null, shoutFading: false, presentingSequence: null } },
  computed: {
    description () {
      const hand = this.table && this.table.hand
      const roll = this.table && this.table.recentRolls && this.table.recentRolls[0]
      return 'Three-dimensional craps table. Point ' + (hand && hand.point ? hand.point : 'off') + (roll && (this.presentingSequence === null || compareDecimal(roll.sequence, this.presentingSequence) < 0) ? '. Recorded dice ' + roll.dice.join(' and ') : '') + '. All wagering controls are available as buttons below.'
    },
    snapshot () {
      const table = this.table || {}
      const hand = table.hand || {}
      const roll = table.recentRolls && table.recentRolls[0]
      // A seven-out can prepare the next hand before this roll's shout finishes.
      // Explicit nulls from the rolled hand still clear moderated text immediately.
      const shout = roll && Object.prototype.hasOwnProperty.call(roll, 'shout') ? roll.shout : hand.shout
      const shooter = roll && Object.prototype.hasOwnProperty.call(roll, 'shooter') ? roll.shooter : hand.shooter
      return { revision: table.revision, phase: table.phase, point: hand.point || null, dice: roll ? roll.dice : null, rollSequence: roll ? roll.sequence : null, outcomes: roll ? roll.outcomes || null : null, shooter: shooter || 'House', shout: shout || null, rollCount: Number(hand.rollCount || 0), madePoints: hand.bonus ? hand.bonus.madePoints : [], seenNumbers: hand.bonus ? hand.bonus.seenNumbers : [], betStacks: table.activity ? table.activity.stacks || [] : [] }
    }
  },
  watch: {
    snapshot (value) { this.updateScene(value) },
    snap () { this.updateScene(this.snapshot) },
    connection () { this.updateScene(this.snapshot) },
    graphics (value) { if (value !== 'ready') this.updateScene(this.snapshot, true) },
    insets (value) { if (this.sceneController && this.sceneController.setInsets) this.sceneController.setInsets(value || {}) }
  },
  mounted () {
    window.addEventListener('keydown', this.cameraShortcut)
    window.addEventListener('obsSourceActiveChanged', this.obsActiveChanged)
    window.addEventListener('obsSourceVisibleChanged', this.obsVisibleChanged)
    document.addEventListener('visibilitychange', this.visibilityChanged)
    this.mountScene()
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.cameraShortcut)
    window.removeEventListener('obsSourceActiveChanged', this.obsActiveChanged)
    window.removeEventListener('obsSourceVisibleChanged', this.obsVisibleChanged)
    document.removeEventListener('visibilitychange', this.visibilityChanged)
    this.cancelPresentation()
    this.sceneDestroyed = true
    if (this.mountAbort) this.mountAbort.abort()
    if (this.sceneController) this.sceneController.dispose()
  },
  methods: {
    cameraShortcut (event) {
      if (event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return
      const target = event.target
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) {
        if (!(target.type === 'range' && this.$el.contains(target))) return
      }
      if (event.key === 'Escape') { this.cameraPanel = false; return }
      if ((event.key === '~' || event.key === '`') && this.cameraSettings) {
        event.preventDefault()
        this.cameraPanel = !this.cameraPanel
      }
    },
    setCameraTilt (value) {
      if (this.sceneController && this.sceneController.setCameraTilt) this.cameraTilt = this.sceneController.setCameraTilt(value)
    },
    visibilityChanged () { this.updateScene(this.snapshot, true) },
    obsActiveChanged (event) { this.obsActive = !event.detail || event.detail.active !== false; this.visibilityChanged() },
    obsVisibleChanged (event) { this.obsVisible = !event.detail || event.detail.visible !== false; this.visibilityChanged() },
    sceneVisible () { return !document.hidden && this.obsActive !== false && this.obsVisible !== false },
    cancelPresentation (clearQueue = true) {
      window.clearTimeout(this.rollTimer)
      window.clearTimeout(this.shoutTimer)
      this.pendingRoll = null
      this.shoutAnnouncement = null
      this.shoutFading = false
      if (clearQueue) this.rollQueue = []
    },
    applyScene (snapshot, snap) {
      this.sceneController.update(snapshot, { snap })
      this.sceneSequence = snapshot.rollSequence
      if (snap) this.rollComplete(this.presentingSequence)
      // A gap's counter arrives before its replacement snapshot. Keep it pending
      // through intermediate snapshots; only the applied live snapshot consumes it.
      if (this.connection === 'live') this.lastSnap = this.snap
    },
    rollComplete (sequence) {
      // A newer completion also retires an older animation skipped by the renderer.
      if (sequence === null || this.presentingSequence === null || compareDecimal(sequence, this.presentingSequence) < 0) return
      const pending = this.pendingRoll && this.pendingRoll.rollSequence
      this.presentingSequence = pending && compareDecimal(pending, sequence) > 0 ? pending : null
      this.$emit('presentation', this.presentingSequence)
    },
    updateScene (snapshot, forceSnap = false) {
      if (!this.sceneController) return
      const shouldSnap = forceSnap || this.connection !== 'live' || this.lastSnap !== this.snap || !this.sceneVisible() || this.graphics !== 'ready'
      if (shouldSnap) {
        this.cancelPresentation()
        this.applyScene(snapshot, true)
        return
      }
      const newRoll = snapshot.dice && snapshot.rollSequence !== null && snapshot.rollSequence !== this.sceneSequence
      if (this.pendingRoll && newRoll) {
        const previousShout = this.pendingRoll.shout
        if (this.pendingRoll.rollSequence === snapshot.rollSequence) this.pendingRoll = snapshot
        else {
          const index = this.rollQueue.findIndex(roll => roll.rollSequence === snapshot.rollSequence)
          if (index >= 0) this.rollQueue[index] = snapshot
          else if (this.rollQueue.length < MAX_QUEUED_ROLLS) this.rollQueue.push(snapshot)
          else { this.updateScene(snapshot, true); return }
        }
        // Historical rolls inherit their hand's current shout. Preserve each
        // accepted non-null text, but honor moderation clears even after a newer roll.
        for (const waiting of [this.pendingRoll, ...this.rollQueue]) {
          if (waiting.rollSequence === snapshot.rollSequence) continue
          const recorded = (this.table.recentRolls || []).find(roll => roll.sequence === waiting.rollSequence)
          if (!recorded) continue
          if (recorded.shout === null) waiting.shout = null
          if (Object.prototype.hasOwnProperty.call(recorded, 'outcomes')) waiting.outcomes = recorded.outcomes
        }
        if (this.pendingRoll.shout !== previousShout) this.releaseRoll()
        return
      }
      if (!newRoll) {
        if (this.pendingRoll) this.cancelPresentation()
        this.applyScene(snapshot, false)
        return
      }
      this.cancelPresentation()
      this.presentRoll(snapshot)
    },
    releaseRoll () {
      const pending = this.pendingRoll
      this.cancelPresentation(false)
      if (!pending || this.sceneDestroyed) return
      if (!this.sceneVisible() || this.connection !== 'live' || this.lastSnap !== this.snap || this.graphics !== 'ready') { this.updateScene(this.snapshot, true); return }
      this.applyScene(pending, false)
      const next = this.rollQueue.shift()
      if (next) this.presentRoll(next)
    },
    presentRoll (snapshot) {
      // A following shout can start while the previous dice finish moving.
      // Keep results hidden from the earliest unfinished throw onward.
      if (this.presentingSequence === null) {
        this.presentingSequence = snapshot.rollSequence
        this.$emit('presentation', snapshot.rollSequence)
      }
      this.pendingRoll = snapshot
      this.shoutAnnouncement = { sequence: snapshot.rollSequence, text: snapshot.shout, shooter: snapshot.shooter.displayName || 'Shooter' }
      this.shoutTimer = window.setTimeout(() => {
        this.shoutFading = true
        this.shoutTimer = window.setTimeout(() => {
          this.shoutAnnouncement = null
          this.shoutFading = false
          this.rollTimer = window.setTimeout(this.releaseRoll, SHOUT_ROLL_DELAY_MS)
        }, SHOUT_FADE_MS)
      }, SHOUT_HOLD_MS)
    },
    async mountScene () {
      this.cancelPresentation()
      this.rollComplete(this.presentingSequence)
      if (this.mountAbort) this.mountAbort.abort()
      if (this.sceneController) this.sceneController.dispose()
      this.sceneController = null
      const abort = new window.AbortController()
      this.mountAbort = abort
      this.graphics = 'loading'
      try {
        const api = await loadSceneModule()
        if (this.sceneDestroyed || abort.signal.aborted) return
        this.cameraSettings = api.CAMERA_TILT || null
        if (this.cameraTilt === null && this.cameraSettings) this.cameraTilt = this.cameraSettings.default
        const scene = await api.mount(this.$refs.surface, {
          protocolVersion: 1,
          initialState: this.snapshot,
          cameraTilt: this.cameraTilt,
          insets: this.insets || {},
          backend: this.$route.query.renderer === 'webgl' ? 'webgl' : 'auto',
          quality: this.$route.query.quality === 'low' ? 'low' : 'auto',
          reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
          signal: abort.signal,
          onRollComplete: sequence => { if (!abort.signal.aborted) this.rollComplete(sequence) },
          onStatus: state => {
            if (abort.signal.aborted) return
            this.graphics = state.status === 'ready' ? 'ready' : state.status === 'error' ? 'failed' : 'loading'
            this.backend = state.backend || ''
          },
          onSelectTarget: this.interactive ? target => this.$emit('select', target) : undefined
        })
        if (abort.signal.aborted || this.sceneDestroyed) { scene.dispose(); return }
        this.sceneController = scene
        this.updateScene(this.snapshot, true)
        this.graphics = 'ready'
      } catch (_) { if (!abort.signal.aborted && !this.sceneDestroyed) this.graphics = 'failed' }
    }
  }
}
</script>
<style>
.craps { text-align: left; color: var(--sl-text); --craps-muted: #b7b8c6; }
.craps * { box-sizing: border-box; }
.craps button, .craps input, .craps select { font: inherit; }
.craps a { color: #f9bb6e; }
.craps button:focus-visible, .craps a:focus-visible, .craps input:focus-visible, .craps select:focus-visible, .craps summary:focus-visible { outline: 3px solid #ffd584; outline-offset: 3px; }
.craps-button { border: 1px solid #555561; border-radius: 8px; background: #25252e; color: #fff; padding: 9px 13px; cursor: pointer; min-height: 42px; }
.craps-button:hover:not(:disabled) { background: #3b3541; border-color: #f9bb6e; }
.craps-button:disabled { opacity: .45; cursor: not-allowed; }
.craps-button--primary { background: #a32b38; border-color: #e06770; font-weight: 700; }
.craps-button[aria-pressed="true"] { background: #533d22; border-color: #f9bb6e; }
.craps-panel { border: 1px solid var(--sl-line); background: var(--sl-surface); border-radius: 14px; padding: 20px; margin-top: 18px; min-width: 0; }
.craps-muted { color: var(--craps-muted); }
.craps-row { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; }
.craps-label { display: flex; flex-direction: column; gap: 7px; font-size: 14px; color: #dadbe3; }
.craps-input { max-width: 100%; min-height: 44px; border: 1px solid #5a5a67; border-radius: 8px; background: #0e0e15; color: #fff; padding: 10px; }
.craps-note { line-height: 1.55; color: var(--craps-muted); font-size: 14px; }
.craps-code { overflow-wrap: anywhere; font-family: monospace; font-size: 12px; }
.craps-scene { position: relative; overflow: hidden; border: 1px solid #59524a; border-radius: 16px; background: radial-gradient(ellipse, #23332b, #11141b); margin-top: 18px; }
.craps-scene__surface { width: 100%; aspect-ratio: 1.9; min-height: 220px; }
.craps-scene__surface canvas { display: block; }
/* Website announcements stay on the felt; OBS announcements fill its display. */
.craps-scene__announcement { position: absolute; inset: 0; z-index: 5; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 24px 4%; pointer-events: none; text-align: center; color: #fff3cb; text-shadow: 0 5px 0 #652019, 0 0 18px #000, 0 0 48px #e5a246; background: radial-gradient(ellipse at center, #17100ae8 0%, #17100a9c 38%, transparent 76%); }
.craps-scene__announcement strong { max-width: 100%; overflow-wrap: anywhere; font-family: var(--sl-font-display); font-size: clamp(40px, 8vw, 152px); line-height: 1.02; letter-spacing: .015em; }
.craps-scene__shooter { max-width: 100%; overflow-wrap: anywhere; font-family: var(--sl-font-display); font-size: clamp(22px, 3vw, 56px); letter-spacing: .06em; text-transform: uppercase; }
.craps-scene__lottery strong { color: #ffe3a0; animation: craps-lottery-glow 2s ease-in-out infinite alternate; }
.craps-scene__lottery-detail { max-width: 85%; font-size: clamp(15px, 1.8vw, 34px); font-weight: 700; color: #fff; text-shadow: 0 2px 12px #000; }
.craps-overlay .craps-scene__announcement { position: fixed; z-index: 1000; gap: 24px; padding: 6vh 4vw; }
.craps-overlay .craps-scene__announcement strong { font-size: clamp(54px, 12vw, 240px); }
.craps-overlay .craps-scene__shooter { font-size: clamp(24px, 4vw, 78px); }
.craps-scene__shout { transition: opacity .6s ease; }
.craps-scene__shout > * { animation: craps-shout-arrive .45s cubic-bezier(.15, 1.4, .4, 1); }
.craps-scene__shout.is-fading { opacity: 0; }
@keyframes craps-shout-arrive { from { opacity: 0; transform: scale(.7) rotate(-3deg); } to { opacity: 1; transform: scale(1) rotate(0); } }
@keyframes craps-lottery-glow { from { text-shadow: 0 5px 0 #652019, 0 0 24px #c88b35; } to { text-shadow: 0 5px 0 #652019, 0 0 56px #e5a246; } }
@media (prefers-reduced-motion: reduce) { .craps-scene__shout > *, .craps-scene__lottery strong { animation: none; } }
.craps-scene__top, .craps-scene__bottom { position: absolute; left: 12px; right: 12px; z-index: 2; pointer-events: none; }
.craps-scene__top { top: 10px; }
.craps-scene__bottom { bottom: 10px; }
.craps-scene__top > *, .craps-scene__bottom > * { pointer-events: auto; }
.craps-scene__top .craps-banner, .craps-scene__bottom .craps-tip { margin-top: 0; }
.craps-scene__notice { position: absolute; top: 16px; left: 16px; right: 16px; padding: 12px; background: #15151ce8; border: 1px solid #7f766a; border-radius: 8px; }
.craps-scene__backend { position: absolute; bottom: 12px; right: 14px; font: 11px monospace; color: #d4d3ce; pointer-events: none; }
.craps-scene__camera { position: absolute; z-index: 4; top: 12px; right: 12px; width: 300px; max-width: calc(100% - 24px); padding: 14px; border: 1px solid #81735e; border-radius: 12px; background: #15151cf5; color: #f4ead4; box-shadow: 0 8px 24px #0006; }
.craps-scene__camera-heading, .craps-scene__camera-scale, .craps-scene__camera-footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.craps-scene__camera-heading { margin-bottom: 10px; }
.craps-scene__camera input { width: 100%; accent-color: #f9bb6e; cursor: pointer; }
.craps-scene__camera-scale, .craps-scene__camera-footer { font-size: 12px; color: #b7b8c6; }
.craps-scene__camera-footer { margin-top: 12px; }
.craps-scene__camera .craps-button { min-height: 32px; padding: 4px 10px; }
@media (max-width: 700px) { .craps-panel { padding: 15px; } .craps-scene__surface { min-height: 400px; } }
</style>



// WEBPACK FOOTER //
// src/components/craps/Craps-Scene.vue